<template>
  <div class="dashboard_page_container">
    <a :href="warningUrl">
      <div class="warning" v-if="isWarningVisible">
        <b>Atención: </b>{{ warningMessage }}
      </div>
    </a>
    <h2 v-if="amountPendingToSendToStripe !== 0" style="margin-left: 10px;">Tienes {{ formatAmount(Number(amountPendingToSendToStripe)) }}€ pendientes de enviar de Click And Plan a tu cuenta de Stripe</h2>
    <h2 style="margin-left: 10px;">Tienes {{ formatAmount(Number(pendingAmountInStripe)) }}€ pendientes de enviar de tu cuenta de Stripe a tu cuenta bancaria</h2>
    <a :href="stripeUrl" target="_blank" style="margin-left: 10px;">Accede a tu panel de Stripe</a>
    <h3 style="margin-left: 10px; margin-top: 50px;">Facturas</h3>
    <table>
      <thead>
        <tr>
          <th>Fecha</th>
          <th>Importe total</th>
          <th>Gastos de gestión</th>
          <th>Total liquidado</th>
        </tr>
      </thead>
      <tbody>
      <tr v-if="(invoices.length === 0 && !loadingStore.isLoading)">
        <td colspan="4" style="text-align: center; padding: 20px;">No tienes facturas emitidas.</td>
      </tr> 
      <tr
        v-for="(item, index) in invoices"
          :key="index"
          :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          @click="invoiceClicked(item)"
        >
          <td>{{ formatearMesAno(item.month) }}</td>
          <td>{{ formatAmount(item.amount) }}€</td>
          <td>{{ formatAmount(item.gg) }}€</td>
          <td>{{ formatAmount(item.amount - item.gg) }}€</td>
        </tr>

      </tbody>
    </table>

    <h3 style="margin-left: 10px; margin-top: 50px;">Pagos</h3>
    <table>
      <thead>
        <tr>
          <th>Fecha envío</th>
          <th>Fecha estimada recepción</th>
          <th>Cantidad</th>
        </tr>
      </thead>
      <tbody>
      <tr v-if="(stripeTransfers.length === 0 && !loadingStore.isLoading)">
        <td colspan="3" style="text-align: center; padding: 20px;">No tienes transferencias pendientes.</td>
      </tr> 
      <tr
        v-for="(item, index) in stripeTransfers"
          :key="index"
          :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          @click="stripePaymentClicked(item)"
        >
          <td>{{ item.created_date }}</td>
          <td>{{ item.arrival_date }}</td>
          <td>{{ formatAmount(item.amount) }}€</td>
        </tr>

      </tbody>
    </table>
    <p style="margin-top: 50px; margin-left: 30px; margin-right: 30px; font-size: 14px; color: var(--text-color-light);">Stripe es nuestra herramienta para gestionar los pagos. Los usuarios pagan nuestros planes a través de esta herramienta, que a su vez gestiona los pagos que recibes tú como partner nuestro.<br><br>Si has canjeado algun código en el día de hoy es posible que hasta mañana no te aparezca en este panel. Aparecerá una vez Stripe programe la transferencia de dinero a tu cuenta.</p>
    <ErrorPopup :errorMessage="error" />
    <PaymentDetailsPopup :paymentInfo="paymentInfo" :movements="movements"/>
  </div>

</template>

<script setup>
import { ref } from "vue";
import { useRouter } from 'vue-router';
import { useLoadingStore } from "@/stores/loadingStore";
import { useNavigationStore } from "@/stores/navigationStore";
import { refreshToken } from "@/services/authService";

import ErrorPopup from "@/components/common/ErrorPopup.vue";
import PaymentDetailsPopup from "./common/PaymentDetailsPopup.vue";

const error = ref(null);
const loadingStore = useLoadingStore();
const navigationStore = useNavigationStore();
const router = useRouter();
const isWarningVisible = ref(false);
const warningUrl = ref("");
const warningMessage = ref("");
const stripeUrl = ref("");
const amountPendingToSendToStripe = ref(0);
const pendingAmountInStripe = ref(0);
const stripeTransfers = ref([]);
const paymentInfo = ref(null);
const movements = ref(null);
const invoices = ref([]);

const getStripeStatus = async () => {
  loadingStore.setLoading(true);
  error.value = null;
  warningUrl.value = null;
  warningMessage.value = null;
  isWarningVisible.value = false;

  try {
    let response = await fetchStripeStatus();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchStripeStatus();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo el estado de tu cuenta.";
    }

    const data = await response.json();
    navigationStore.setUserType(data.isOptionalCompany ? "optional" : "main");

    if (data.warningMessage != null) {
      warningUrl.value = data.warningUrl;
      warningMessage.value = data.warningMessage;
      isWarningVisible.value = true;
    } else {
      isWarningVisible.value = false;
    }
    amountPendingToSendToStripe.value = Number(data.amountPendingToSendToStripe);
    pendingAmountInStripe.value = Number(data.amountPendingToBeTransfered);
    stripeTransfers.value = data.stripeTransfers;
    invoices.value = data.invoices;
  } catch (exception) {
      // do nothing
  } finally {
    loadingStore.setLoading(false);
  }
  getStripeDashboardUrl();
};

const getStripeDashboardUrl = async () => {
  stripeUrl.value = "";

  try {
    let response = await fetchStripeDashboardUrl();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchStripeDashboardUrl();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    const data = await response.json();

    if (data.url != null) {
      stripeUrl.value = data.url;
    }
  } catch (exception) {
      // do nothing
  }
};

async function fetchStripeDashboardUrl() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_dashboard_url", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id')
    }),
  });
}

async function fetchStripeStatus() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_account_status", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id')
    }),
  });
}

navigationStore.setNavigationPosition(2);
getStripeStatus();

function formatAmount(valor) {
  return new Intl.NumberFormat('es-ES', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }).format(valor);
}

async function invoiceClicked(item){
  loadingStore.setLoading(true);
  try {
    let response = await fetchInvoice(item.month);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchInvoice(item.month);
      } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
      }
    }

    loadingStore.setLoading(false);

    if (!response.ok) {
      error.value = "Se ha producido un error descargando la factura.";
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch (exception) {
    error.value = exception.message;
    paymentInfo.value = null;
  } finally {
    loadingStore.setLoading(false);
  }
}

async function fetchInvoice(month) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_invoice", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id'),
      "date": month
    }),
  });
}

async function stripePaymentClicked(item) {
  paymentInfo.value = null;
  movements.value = null;
  loadingStore.setLoading(true);

  try {
    let response = await fetchPayoutInfo(item.id);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchPayoutInfo(item.id);
      } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
      }
    }

    loadingStore.setLoading(false);

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo la información del pago.";
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
    } else {
      error.value = null;
      paymentInfo.value = item;
      movements.value = data.datos;
      history.pushState({ popupOpen: true }, "");
    }
  } catch (exception) {
    error.value = exception.message;
    paymentInfo.value = null;
  } finally {
    loadingStore.setLoading(false);
  }
}

async function fetchPayoutInfo(id) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_payout_info", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id'),
      "payout_id": id
    }),
  });
}

function formatearMesAno(mesAno) {
  const [anio, mes] = mesAno.split("-");
  const fecha = new Date(anio, mes - 1); // Mes va de 0 (enero) a 11 (diciembre)

  return new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' })
    .format(fecha)
    .replace(/^./, str => str.toUpperCase()); // Capitaliza la primera letra
}

</script>

<style scoped>
table {
  border-radius: 10px;
  overflow: hidden;
  margin-top: 30px;
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: var(--gray);
  color: white;
  width: 25%;
  border: 0px;
}

tr{
  cursor: pointer;
}

td {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
}

.bg-gray-100 {
  background-color: var(--almost-white);
}

.bg-white {
  background-color: white;
}

.opaque {
  opacity: 1;
}

.translucid {
  opacity: 0.5;
}

.filter-search {
  width: 300px;
}

.warning {
  border-radius: 10px;
  background-color: #ffe00069;
  color: var(--gray);
  margin-top: 30px;
  padding: 10px 20px 8px 20px;
}

</style>
